import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { Badge } from '@shared/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select'
import { Label } from '@shared/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faSearch, 
  faFilter,
  faDownload,
  faEye,
  faClipboardList,
  faUser,
  faCog,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons'
import { PageHeader } from '@shared/components/ui/page-header'
import AuditLogViewerDialog from '@/components/AuditLogViewerDialog'
import { apiUrl } from '@/lib/api'

interface AuditLogEntry {
  id: string
  timestamp: string
  user: string
  action: string
  resource: string
  details: string
  ip_address: string
  user_agent: string
  severity: 'low' | 'medium' | 'high' | 'critical'
}

const fetchAuditLogs = async (params: URLSearchParams): Promise<AuditLogEntry[]> => {
  const { auth } = await import('@/lib/firebase')
  const idToken = await auth.currentUser?.getIdToken()
  const response = await fetch(apiUrl(`/api/admin/audit-logs?${params.toString()}`), { headers: idToken ? { 'Authorization': `Bearer ${idToken}` } : {} })
  if (!response.ok) throw new Error('Failed to fetch audit logs')
  const data = await response.json()
  return data.logs || []
}

const AuditLogPage: React.FC = () => {
  const [q, setQ] = useState('')
  const [actor, setActor] = useState('')
  const [actionType, setActionType] = useState('all')
  const [action, setAction] = useState('')
  const [resource, setResource] = useState('')
  const [resourceId, setResourceId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [severity, setSeverity] = useState('all')
  const [ip, setIp] = useState('')
  const [submittedParams, setSubmittedParams] = useState('')
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  
  const invalidDateRange = useMemo(() => {
    if (!from || !to) return false
    try { return new Date(from).getTime() > new Date(to).getTime() } catch { return false }
  }, [from, to])

  const { data: auditLogs = [], isLoading, error, refetch } = useQuery({
    queryKey: ['auditLogs', submittedParams],
    queryFn: async () => {
      const searchParams: Record<string, string> = {}
      for (const part of submittedParams.split('&')) {
        if (!part) continue
        const [k, v] = part.split('=')
        if (k) searchParams[decodeURIComponent(k)] = decodeURIComponent(v || '')
      }
      const p = new URLSearchParams()
      if (searchParams.from) p.set('from', searchParams.from)
      if (searchParams.to) p.set('to', searchParams.to)
      if (searchParams.actor) p.set('actor', searchParams.actor)
      if (searchParams.action) p.set('action', searchParams.action)
      if (searchParams.resource) p.set('resource', searchParams.resource)
      if (searchParams.resourceId) p.set('resourceId', searchParams.resourceId)
      if (searchParams.severity) p.set('severity', searchParams.severity)
      if (searchParams.ip) p.set('ip', searchParams.ip)
      return fetchAuditLogs(p)
    },
    retry: false
  })

  const handleSearch = () => {
    const p = new URLSearchParams()
    if (from) p.set('from', new Date(from).toISOString())
    if (to) p.set('to', new Date(to).toISOString())
    if (actor) p.set('actor', actor)
    if (actionType !== 'all') p.set('actionType', actionType)
    if (action) p.set('action', action)
    if (resource) p.set('resource', resource)
    if (resourceId) p.set('resourceId', resourceId)
    if (severity !== 'all') p.set('severity', severity)
    if (ip) p.set('ip', ip)
    setSubmittedParams(p.toString())
  }

  const handleClear = () => {
    setQ('')
    setActor('')
    setActionType('all')
    setAction('')
    setResource('')
    setResourceId('')
    setIp('')
    setFrom('')
    setTo('')
    setSeverity('all')
    setSubmittedParams('')
  }

  const logs = useMemo(() => {
    const list = (auditLogs || []) as AuditLogEntry[]
    if (!q) return list
    const needle = q.toLowerCase()
    return list.filter((l) => {
      const hay = `${l.user || ''}|${l.action}|${l.resource}|${l.details || ''}`.toLowerCase()
      return hay.includes(needle)
    })
  }, [auditLogs, q])

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500'
      case 'high': return 'bg-orange-500'
      case 'medium': return 'bg-yellow-500'
      case 'low': return 'bg-green-500'
      default: return 'bg-gray-500'
    }
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATE': return faUser
      case 'UPDATE': return faCog
      case 'DELETE': return faShieldAlt
      case 'LOGIN': return faUser
      case 'BACKUP': return faClipboardList
      case 'MAINTENANCE': return faCog
      default: return faClipboardList
    }
  }

  const handleLogClick = (log: AuditLogEntry) => {
    setSelectedLog(log)
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setSelectedLog(null)
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Audit Log" description="System activity and admin action logs">
        <Button variant="outline" onClick={()=>refetch()}>
          Refresh
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-3 lg:col-span-3">
              <Label htmlFor="q">Search</Label>
              <Input id="q" placeholder="Free text" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="md:col-span-3 lg:col-span-3">
              <Label htmlFor="actor">Actor</Label>
              <Input id="actor" placeholder="Name, email, or ID" value={actor} onChange={(e) => setActor(e.target.value)} />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="actionType">Action Type</Label>
              <Select value={actionType} onValueChange={(v) => setActionType(v)}>
                <SelectTrigger id="actionType">
                  <SelectValue placeholder="Action Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="upsert">Upsert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="action">Action (key)</Label>
              <Input id="action" placeholder="e.g. user.update" value={action} onChange={(e) => setAction(e.target.value)} />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="resource">Resource Type</Label>
              <Input id="resource" placeholder="e.g. user" value={resource} onChange={(e) => setResource(e.target.value)} />
            </div>
            <div className="md:col-span-3 lg:col-span-3">
              <Label htmlFor="resourceId">Resource ID</Label>
              <Input id="resourceId" placeholder="ID" value={resourceId} onChange={(e) => setResourceId(e.target.value)} />
            </div>
            <div className="md:col-span-3 lg:col-span-2">
              <Label htmlFor="ip">IP Address</Label>
              <Input id="ip" placeholder="127.0.0.1" value={ip} onChange={(e) => setIp(e.target.value)} />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="md:col-span-2 lg:col-span-2">
              <Label htmlFor="severity">Severity</Label>
              <Select value={severity} onValueChange={(v) => setSeverity(v)}>
                <SelectTrigger id="severity">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 md:col-span-6 lg:col-span-1 flex justify-end items-end">
              <Button variant="outline" onClick={handleClear}>Clear</Button>
            </div>
          </div>
          {invalidDateRange && (
            <div className="mt-3">
              <Alert variant="destructive">
                <AlertTitle>Invalid date range</AlertTitle>
                <AlertDescription>
                  The &quot;To&quot; date is earlier than the &quot;From&quot; date. Please adjust the range.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={handleSearch} disabled={invalidDateRange}>Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer hover:bg-accent/40" onClick={() => handleLogClick(l)}>
                    <TableCell className="font-mono text-sm">{new Date(l.timestamp).toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={getActionIcon(l.action)} className="h-4 w-4 text-muted-foreground" />
                        {l.user}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{l.action}</Badge></TableCell>
                    <TableCell>{l.resource}</TableCell>
                    <TableCell><Badge className={getSeverityColor(l.severity)}>{l.severity}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{l.ip_address || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!isLoading && logs.length === 0 && (
            <div className="text-center py-6 text-muted-foreground">No audit logs</div>
          )}
        </CardContent>
      </Card>

      <AuditLogViewerDialog 
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        logEntry={selectedLog}
      />
    </div>
  )
}

export default AuditLogPage
